type ResizeVariant = '200x200' | '600x600';

const DEFAULT_RESIZE_VARIANT: ResizeVariant = '200x200';

function fileNameWithResizeSuffix(fileName: string, variant: ResizeVariant): string {
  const resizeSuffix = `_${variant}`;
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === fileName.length - 1) {
    return `${fileName}${resizeSuffix}`;
  }
  return `${fileName.slice(0, lastDot)}${resizeSuffix}${fileName.slice(lastDot)}`;
}

function siblingThumbnailObjectPath(objectPath: string, variant: ResizeVariant): string | null {
  if (objectPath.includes('/thumbnail/')) return null;
  const lastSlash = objectPath.lastIndexOf('/');
  const dir = lastSlash >= 0 ? objectPath.slice(0, lastSlash) : '';
  const file = lastSlash >= 0 ? objectPath.slice(lastSlash + 1) : objectPath;
  const sizedFile = fileNameWithResizeSuffix(file, variant);
  return dir ? `${dir}/thumbnail/${sizedFile}` : `thumbnail/${sizedFile}`;
}

function firebaseStorageThumbnailUrl(originalUrl: string, variant: ResizeVariant): string | null {
  const m = originalUrl.match(/^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\/o\/([^?]+)(\?[^#]*)?$/i);
  if (!m) return null;
  const bucket = m[1];
  let objectPath: string;
  try {
    objectPath = decodeURIComponent(m[2]);
  } catch {
    return null;
  }
  const thumbObjectPath = siblingThumbnailObjectPath(objectPath, variant);
  if (!thumbObjectPath) return null;
  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(thumbObjectPath)}?alt=media`;
}

function gcsStorageThumbnailUrl(originalUrl: string, variant: ResizeVariant): string | null {
  const m = originalUrl.match(/^https:\/\/storage\.googleapis\.com\/([^/]+)\/([^?#]+)/i);
  if (!m) return null;
  const bucket = m[1];
  let objectPath: string;
  try {
    objectPath = decodeURIComponent(m[2]);
  } catch {
    objectPath = m[2];
  }
  const thumbObjectPath = siblingThumbnailObjectPath(objectPath, variant);
  if (!thumbObjectPath) return null;
  return `https://storage.googleapis.com/${bucket}/${thumbObjectPath.split('/').map(encodeURIComponent).join('/')}`;
}

export type ImageThumbSource =
  | string
  | {
      thumbnail?: string | null;
      src?: string | null;
    };

export function getImageThumbSrcList(
  source: ImageThumbSource,
  options?: { variant?: ResizeVariant }
): string[] {
  const variant = options?.variant ?? DEFAULT_RESIZE_VARIANT;
  const fileUrl =
    typeof source === 'string'
      ? source.trim()
      : (typeof source.thumbnail === 'string' && source.thumbnail.trim()) ||
        (typeof source.src === 'string' && source.src.trim()) ||
        '';
  if (!fileUrl) return [];
  if (fileUrl.includes('/imagedelivery.net')) {
    const thumbVariant = fileUrl.replace('/public', '/thumbnail');
    return thumbVariant !== fileUrl ? [thumbVariant, fileUrl] : [fileUrl];
  }
  const firebaseThumb = firebaseStorageThumbnailUrl(fileUrl, variant);
  if (firebaseThumb) return [firebaseThumb, fileUrl];
  const gcsThumb = gcsStorageThumbnailUrl(fileUrl, variant);
  if (gcsThumb) return [gcsThumb, fileUrl];
  return [fileUrl];
}

export function getImageThumbPrimaryUrl(source: ImageThumbSource): string {
  const list = getImageThumbSrcList(source);
  return list[0] ?? '';
}

export function getImageThumbPrimaryUrlByVariant(source: ImageThumbSource, variant: ResizeVariant): string {
  const list = getImageThumbSrcList(source, { variant });
  return list[0] ?? '';
}
